import { useFetcher } from '@remix-run/react'
import { useState } from 'react'

export function UserActionsMenu({
	targetUserId,
	targetUsername,
	isBlocked,
}: {
	targetUserId: string
	targetUsername: string
	isBlocked: boolean
}) {
	const fetcher = useFetcher()
	const [open, setOpen] = useState(false)
	const [showReport, setShowReport] = useState(false)
	const [showBlockConfirm, setShowBlockConfirm] = useState(false)
	const [reason, setReason] = useState('')
	const [context, setContext] = useState('')

	const optimisticBlocked =
		fetcher.formData?.get('intent') === 'block'
			? true
			: fetcher.formData?.get('intent') === 'unblock'
				? false
				: isBlocked

	return (
		<>
			<div className="relative">
				<button
					type="button"
					onClick={() => setOpen(!open)}
					className="rounded-full p-1.5 text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground"
					title="More actions"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
						fill="currentColor"
						className="h-5 w-5"
					>
						<path d="M3 10a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm5.5 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Zm5.5 0a1.5 1.5 0 1 1 3 0 1.5 1.5 0 0 1-3 0Z" />
					</svg>
				</button>

				{open && (
					<>
						{/* Backdrop to close menu */}
						<div
							className="fixed inset-0 z-40"
							onClick={() => setOpen(false)}
						/>
						<div className="absolute right-0 z-50 mt-1 w-48 rounded-lg border bg-background py-1 shadow-lg">
							<button
								type="button"
								className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-muted"
								onClick={() => {
									setOpen(false)
									if (optimisticBlocked) {
										fetcher.submit(
											{
												intent: 'unblock',
												targetUserId,
											},
											{
												method: 'POST',
												action: '/resources/user-actions',
											},
										)
									} else {
										setShowBlockConfirm(true)
									}
								}}
							>
								{optimisticBlocked ? (
									<>
										<span>Unblock</span>
										<span className="text-muted-foreground">
											@{targetUsername}
										</span>
									</>
								) : (
									<>
										<span className="text-red-500">
											Block
										</span>
										<span className="text-muted-foreground">
											@{targetUsername}
										</span>
									</>
								)}
							</button>
							<button
								type="button"
								className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-500 hover:bg-muted"
								onClick={() => {
									setOpen(false)
									setShowReport(true)
								}}
							>
								Report User
							</button>
						</div>
					</>
				)}
			</div>

			{/* Block confirmation modal */}
			{showBlockConfirm && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="w-full max-w-sm rounded-xl border bg-background p-6 shadow-lg">
						<h3 className="text-lg font-semibold">
							Block @{targetUsername}?
						</h3>
						<p className="mt-2 text-sm text-muted-foreground">
							They won't be able to send you messages. They
							won't be notified that you blocked them.
						</p>
						<div className="mt-5 flex justify-end gap-3">
							<button
								type="button"
								onClick={() => setShowBlockConfirm(false)}
								className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
							>
								Cancel
							</button>
							<button
								type="button"
								onClick={() => {
									fetcher.submit(
										{
											intent: 'block',
											targetUserId,
										},
										{
											method: 'POST',
											action: '/resources/user-actions',
										},
									)
									setShowBlockConfirm(false)
								}}
								className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600"
							>
								Block
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Report modal */}
			{showReport && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
					<div className="w-full max-w-md rounded-xl border bg-background p-6 shadow-lg">
						<h3 className="text-lg font-semibold">
							Report @{targetUsername}
						</h3>
						<p className="mt-2 text-sm text-muted-foreground">
							Let us know why you're reporting this user. An
							admin will review your report.
						</p>
						<div className="mt-4 space-y-3">
							<div>
								<label
									htmlFor="report-reason"
									className="text-sm font-medium"
								>
									Reason
								</label>
								<select
									id="report-reason"
									value={reason}
									onChange={(e) =>
										setReason(e.target.value)
									}
									className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
								>
									<option value="">
										Select a reason...
									</option>
									<option value="harassment">
										Harassment or bullying
									</option>
									<option value="spam">
										Spam or scam
									</option>
									<option value="inappropriate">
										Inappropriate content
									</option>
									<option value="impersonation">
										Impersonation
									</option>
									<option value="other">Other</option>
								</select>
							</div>
							<div>
								<label
									htmlFor="report-context"
									className="text-sm font-medium"
								>
									Additional details (optional)
								</label>
								<textarea
									id="report-context"
									value={context}
									onChange={(e) =>
										setContext(e.target.value)
									}
									rows={3}
									placeholder="Any additional context that might help us review this report..."
									className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground"
								/>
							</div>
						</div>
						<div className="mt-5 flex justify-end gap-3">
							<button
								type="button"
								onClick={() => {
									setShowReport(false)
									setReason('')
									setContext('')
								}}
								className="rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted"
							>
								Cancel
							</button>
							<button
								type="button"
								disabled={!reason}
								onClick={() => {
									fetcher.submit(
										{
											intent: 'report',
											targetUserId,
											reason,
											context,
										},
										{
											method: 'POST',
											action: '/resources/user-actions',
										},
									)
									setShowReport(false)
									setReason('')
									setContext('')
								}}
								className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
							>
								Submit Report
							</button>
						</div>
					</div>
				</div>
			)}
		</>
	)
}
